import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { DashboardShell } from '../components/brand/DashboardShell';
import {
  Building2, Plus, ExternalLink, RefreshCw, ShieldCheck, Banknote, LayoutGrid,
  Users, Activity, X, Copy, Check,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * /admin/partners — Blueprint Decision #20.
 *
 * Admin-only management for SNF/Hospital partner program. Create a
 * partner; CaraLoom mints a unique slug + co-branded public intake URL.
 * Partner case managers submit referrals at /partners/<slug>/refer
 * without an account.
 */
export default function AdminPartners() {
  const { getAuthHeader } = useAuth();
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState(null);

  const [form, setForm] = useState({
    slug: '',
    facility_name: '',
    contact_email: '',
    hero_message: 'Send discharge referrals to CaraLoom in 60 seconds.',
    color_accent: '#0E3D3A',
    monthly_referral_cap: 250,
  });

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/partners`, getAuthHeader());
      setPartners(data.partners || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load partners');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const update = (k) => (e) => {
    let v = e.target.value;
    if (k === 'slug') v = v.toLowerCase().replace(/[^a-z0-9-]/g, '-').slice(0, 40);
    if (k === 'monthly_referral_cap') v = Math.max(1, Number(v));
    setForm((f) => ({ ...f, [k]: v }));
  };

  const create = async () => {
    if (!form.slug || !form.facility_name) {
      toast.error('Slug and facility name are required.');
      return;
    }
    setCreating(true);
    try {
      const payload = { ...form };
      await axios.post(`${API}/admin/partners`, payload, getAuthHeader());
      toast.success(`Partner ${form.facility_name} created`);
      setShowCreate(false);
      setForm({ ...form, slug: '', facility_name: '' });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Create failed');
    } finally {
      setCreating(false);
    }
  };

  const intakeUrl = (slug) => `${window.location.origin}/partners/${slug}/refer`;

  const copyUrl = async (slug) => {
    try {
      await navigator.clipboard.writeText(intakeUrl(slug));
      setCopied(slug);
      setTimeout(() => setCopied(null), 1800);
    } catch (e) { /* ignore */ }
  };

  const navItems = [
    { icon: LayoutGrid, label: 'Partners', active: true },
    { icon: ShieldCheck, label: 'Trust Portal',  href: '/trust' },
    { icon: Activity,    label: 'Executive',     href: '/operations/executive' },
    { icon: Users,       label: 'Workforce',     href: '/agency/find-clinicians' },
    { icon: Banknote,    label: 'Revenue Cycle', href: '/clinician/billing' },
  ];

  return (
    <DashboardShell
      navItems={navItems}
      title="Partner Program"
      subtitle="Co-branded discharge portals for SNFs and hospital case-management teams."
      testId="admin-partners-page"
      rightSlot={
        <div className="flex gap-2">
          <Button onClick={load} className="cn-btn cn-btn-ghost" disabled={loading} data-testid="admin-partners-refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button onClick={() => setShowCreate((s) => !s)} className="cn-btn cn-btn-primary" data-testid="admin-partners-toggle-create">
            {showCreate ? <X className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            {showCreate ? 'Cancel' : 'New partner'}
          </Button>
        </div>
      }
    >
      {/* CREATE PANEL */}
      {showCreate && (
        <div className="cn-card p-6 mb-6" data-testid="admin-partners-create-form">
          <h3 className="font-display font-semibold text-lg text-cn-text dark:text-white mb-4">Create partner</h3>
          <div className="grid lg:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-cn-text-muted mb-1.5" htmlFor="partner-form-name">Facility name</label>
              <input id="partner-form-name" className="cn-input w-full" value={form.facility_name} onChange={update('facility_name')} data-testid="partner-form-name" />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-cn-text-muted mb-1.5" htmlFor="partner-form-slug">URL slug (3-40 chars · a-z, 0-9, -)</label>
              <input id="partner-form-slug" className="cn-input w-full font-mono text-sm" value={form.slug} onChange={update('slug')} placeholder="memorial-hospital" data-testid="partner-form-slug" />
            </div>
            <div className="lg:col-span-2">
              <label className="block text-xs font-bold uppercase tracking-wider text-cn-text-muted mb-1.5" htmlFor="partner-form-hero">Hero message (one line)</label>
              <input id="partner-form-hero" className="cn-input w-full" value={form.hero_message} onChange={update('hero_message')} data-testid="partner-form-hero" />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-cn-text-muted mb-1.5" htmlFor="partner-form-color">Accent color</label>
              <input id="partner-form-color" type="color" className="cn-input w-full !p-1 h-10" value={form.color_accent} onChange={update('color_accent')} data-testid="partner-form-color" />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-cn-text-muted mb-1.5" htmlFor="partner-form-cap">Monthly referral cap</label>
              <input id="partner-form-cap" type="number" className="cn-input w-full" value={form.monthly_referral_cap} onChange={update('monthly_referral_cap')} data-testid="partner-form-cap" />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-cn-text-muted mb-1.5" htmlFor="partner-form-email">Primary contact email (optional)</label>
              <input id="partner-form-email" type="email" className="cn-input w-full" value={form.contact_email} onChange={update('contact_email')} data-testid="partner-form-email" />
            </div>
          </div>
          <div className="mt-4">
            <Button onClick={create} disabled={creating} className="cn-btn cn-btn-primary" data-testid="partner-form-submit">
              {creating ? 'Creating…' : 'Create partner'}
            </Button>
          </div>
        </div>
      )}

      {/* PARTNER LIST */}
      {loading && partners.length === 0 ? (
        <div className="cn-card h-32 animate-pulse" />
      ) : partners.length === 0 ? (
        <div className="cn-card p-10 text-center" data-testid="admin-partners-empty">
          <Building2 className="w-10 h-10 text-cn-text-muted/40 mx-auto mb-3" />
          <p className="font-display font-semibold text-cn-text">No partners yet</p>
          <p className="text-sm text-cn-text-muted mt-1">Create your first SNF or hospital partner to start receiving referrals.</p>
        </div>
      ) : (
        <ul className="space-y-3" data-testid="admin-partners-list">
          {partners.map((p) => (
            <li key={p.id} className="cn-card p-5 flex items-center gap-4" data-testid={`partner-row-${p.slug}`}>
              <span className="w-10 h-10 rounded-xl flex items-center justify-center text-white shrink-0" style={{ background: p.color_accent || '#0E3D3A' }}>
                <Building2 className="w-5 h-5" />
              </span>
              <div className="flex-1 min-w-0">
                <h3 className="font-display font-semibold text-cn-text dark:text-white truncate">{p.facility_name}</h3>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-xs font-mono text-cn-text-muted truncate">{intakeUrl(p.slug)}</span>
                  <button
                    type="button"
                    onClick={() => copyUrl(p.slug)}
                    className="text-cn-primary hover:text-cn-accent"
                    aria-label="Copy intake URL"
                    data-testid={`partner-copy-${p.slug}`}
                  >
                    {copied === p.slug ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-xs text-cn-text-muted">
                  {(p.month_referral_count || 0)} / {p.monthly_referral_cap} this month
                </div>
                <div className={`text-xs font-semibold mt-0.5 ${p.disabled ? 'text-red-600' : 'text-emerald-700'}`}>
                  {p.disabled ? 'Disabled' : 'Active'}
                </div>
              </div>
              <a
                href={intakeUrl(p.slug)}
                target="_blank"
                rel="noopener noreferrer"
                className="cn-btn cn-btn-ghost !py-2 shrink-0"
                data-testid={`partner-open-${p.slug}`}
              >
                <ExternalLink className="w-4 h-4" />
              </a>
            </li>
          ))}
        </ul>
      )}
    </DashboardShell>
  );
}
